import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode
} from 'react';

import {
  calculateLiveTotal,
  collectSquadOptaIdsByMatch,
  collectUniqueMatchIds,
  isAnyChannelLive
} from './aggregation';
import { MatchSubscriber, type SubscribedMatchState } from './match-subscriber';
import { narrowActiveChip, type LiveTeamPlayer } from './types';
import { useLiveTeam } from '@hooks/useLiveTeam';
import type { LiveTeamData } from '@api-service/live-team';
import type { GetAccessToken } from '@lib/live/connection/realtime-client';
import { resolveRealtimeAccessToken } from '@lib/realtime/realtime-access-token';

type TeamLivePhase = 'IDLE' | 'LOADING' | 'READY' | 'ERROR';

type TeamLiveContextValue = {
  phase: TeamLivePhase;
  fplId: string;
  liveTeam: LiveTeamData | null;
  picks: LiveTeamPlayer[];
  matchStates: Record<string, SubscribedMatchState>;
  liveTotal: number;
  isLive: boolean;
};

const DEFAULT_VALUE: TeamLiveContextValue = {
  phase: 'IDLE',
  fplId: '',
  liveTeam: null,
  picks: [],
  matchStates: {},
  liveTotal: 0,
  isLive: false
};

const TeamLiveContext = createContext<TeamLiveContextValue>(DEFAULT_VALUE);

export const useTeamLive = (): TeamLiveContextValue =>
  useContext(TeamLiveContext);

type TeamLiveProviderProps = {
  fplId: string;
  accessToken: string | null;
  isInMaintenanceMode?: boolean;
  /**
   * A fabricated squad from the simulation harness. When supplied the provider
   * renders it instead of the viewer's real team and never calls `/live/team`.
   *
   * Nothing else changes: the squad is the same shape that endpoint returns,
   * so channel subscription, availability, auto-subs, the running total, the
   * tiles and the simulated indicator all run against it untold. That is the
   * point, since those are the paths a simulation exists to exercise.
   */
  liveTeamOverride?: LiveTeamData | null;
  /**
   * An override is still being resolved and may yet arrive.
   *
   * The harness restores a squad from the URL or storage in an effect, so the
   * first client render has no override even when one exists. Without this the
   * provider would fetch `/live/team` in that gap, and pre-season that answers
   * 404: react-query keeps `isError` set after a query is disabled, so the
   * error would outlive the request that caused it and hold the pitch in ERROR
   * behind a perfectly good squad.
   */
  isLiveTeamOverridePending?: boolean;
  children: ReactNode;
};

export const TeamLiveProvider = ({
  fplId,
  accessToken,
  isInMaintenanceMode = false,
  liveTeamOverride = null,
  isLiveTeamOverridePending = false,
  children
}: TeamLiveProviderProps) => {
  const {
    liveTeam: fetchedLiveTeam,
    liveTeamIsLoading,
    isLiveTeamError
  } = useLiveTeam({
    fplId,
    accessToken: accessToken ?? '',
    isInMaintenanceMode,
    isSkipped: liveTeamOverride !== null || isLiveTeamOverridePending
  });

  const liveTeam = liveTeamOverride ?? fetchedLiveTeam;

  const [matchStates, setMatchStates] = useState<
    Record<string, SubscribedMatchState>
  >({});

  const updateMatchState = useCallback(
    (matchId: string, state: SubscribedMatchState) => {
      setMatchStates((previous) => ({ ...previous, [matchId]: state }));
    },
    []
  );

  const removeMatchState = useCallback((matchId: string) => {
    setMatchStates((previous) => {
      if (!(matchId in previous)) return previous;
      const remaining = { ...previous };
      delete remaining[matchId];
      return remaining;
    });
  }, []);

  const picks = useMemo<LiveTeamPlayer[]>(() => {
    if (!liveTeam) return [];
    return [...liveTeam.starting, ...liveTeam.bench];
  }, [liveTeam]);

  const matchIds = useMemo(() => collectUniqueMatchIds(picks), [picks]);

  const squadOptaIdsByMatch = useMemo(
    () => collectSquadOptaIdsByMatch(picks),
    [picks]
  );

  const liveTotal = useMemo(
    () =>
      calculateLiveTotal(
        picks,
        matchStates,
        narrowActiveChip(liveTeam?.activeChip ?? null)
      ),
    [picks, matchStates, liveTeam?.activeChip]
  );

  const isLive = useMemo(() => isAnyChannelLive(matchStates), [matchStates]);

  // An override outranks the query outright rather than relying on a skipped
  // query reporting nothing. A query that errored before it was skipped keeps
  // reporting that error, so reading `isLiveTeamError` first would hold a
  // perfectly good fabricated squad behind a 404 nobody is waiting on.
  const phase: TeamLivePhase = liveTeamOverride
    ? 'READY'
    : isLiveTeamOverridePending
    ? 'LOADING'
    : isLiveTeamError
    ? 'ERROR'
    : liveTeamIsLoading
    ? 'LOADING'
    : liveTeam
    ? 'READY'
    : 'IDLE';

  // Hold the latest accessToken in a ref so the GetAccessToken closure
  // always resolves to the current value, even if the token populates
  // (or refreshes) after the provider has already mounted subscribers.
  // Without this, the closure captures the initial value and the very
  // first snapshot fetch goes out with `Bearer null` before the token
  // arrives.
  const accessTokenRef = useRef(accessToken);
  accessTokenRef.current = accessToken;
  // Ably re-invokes its auth callback for the connection's whole life, which
  // outlives the token this page mounted with. A provably expired token is
  // swapped for a freshly fetched one at mint time, because nothing else on a
  // parked page ever refreshes it (FFH-2675).
  const getAccessToken: GetAccessToken = useCallback(async () => {
    const current = accessTokenRef.current;
    if (!current) {
      throw new Error('Access token not yet available');
    }
    return resolveRealtimeAccessToken(current);
  }, []);

  const value: TeamLiveContextValue = {
    phase,
    fplId,
    liveTeam,
    picks,
    matchStates,
    liveTotal,
    isLive
  };

  // Hold off on mounting subscribers until we actually have a token —
  // bootstrap would otherwise fire its first snapshot fetch with
  // `Authorization: Bearer null` before useAccessToken populates.
  const hasAccessToken = Boolean(accessToken);

  return (
    <TeamLiveContext.Provider value={value}>
      {hasAccessToken &&
        matchIds.map((matchId) => (
          <MatchSubscriber
            key={matchId}
            matchId={matchId}
            playerOptaIds={squadOptaIdsByMatch[matchId] ?? []}
            getAccessToken={getAccessToken}
            onChange={updateMatchState}
            onRemove={removeMatchState}
          />
        ))}
      {children}
    </TeamLiveContext.Provider>
  );
};
